import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../context/Auth';

function ModalAddPP({ closeModal, planToModify }) {
  const { user, paymentPlans, setPaymentPlans, getUserPaymentPlans } = useAuth();
  const API = process.env.REACT_APP_API_URL;
  const [discountPercentage, setDiscountPercentage] = useState(planToModify ? planToModify.discount : '0');
  const [initialPaymentPercentage, setInitialPaymentPercentage] = useState(planToModify ? planToModify.initialPayment : '0');
  const [installmentPercentage, setInstallmentPercentage] = useState(planToModify ? planToModify.monthlyPayment : '0');
  const [deliveryPercentage, setDeliveryPercentage] = useState(planToModify ? planToModify.giveProperty : '0');
  const [hasMonthsNumber, setHasMonthsNumber] = useState((planToModify && planToModify.monthsNumber > 0) ? true : false);
  const [monthsNumber, setMonthsNumber] = useState(planToModify ? planToModify.monthsNumber : '0');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const totalPercentage = parseFloat(initialPaymentPercentage) +
    parseFloat(installmentPercentage) + parseFloat(deliveryPercentage);

  // El nombre del plan es derivado: {enganche}/{mensualidad}/{entrega}[-{descuento}][m{meses}]
  const derivedName = (() => {
    const initial = parseInt(initialPaymentPercentage, 10) || 0;
    const monthly = parseInt(installmentPercentage, 10) || 0;
    const give = parseInt(deliveryPercentage, 10) || 0;
    const discount = parseInt(discountPercentage, 10) || 0;
    const months = hasMonthsNumber ? (parseInt(monthsNumber, 10) || 0) : 0;
    let name = `${initial}/${monthly}/${give}`;
    if (discount > 0 || months > 0) {
      name += '-';
      if (discount > 0) name += `${discount}`;
      if (months > 0) name += `m${months}`;
    }
    return name;
  })();

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    if (['discountPercentage', 'initialPaymentPercentage', 'installmentPercentage', 'deliveryPercentage', 'monthsNumber'].includes(name)) {
      const parsedValue = parseInt(value, 10); // Elimina ceros a la izquierda
      if (!isNaN(parsedValue) && parsedValue >= 0 && parsedValue <= 100) {
        switch (name) {
          case 'discountPercentage':
            setDiscountPercentage(parsedValue.toString()); // Convierte de nuevo a string para mantener la compatibilidad con inputs
            break;
          case 'initialPaymentPercentage':
            setInitialPaymentPercentage(parsedValue.toString());
            break;
          case 'installmentPercentage':
            setInstallmentPercentage(parsedValue.toString());
            break;
          case 'deliveryPercentage':
            setDeliveryPercentage(parsedValue.toString());
            break;
          case 'monthsNumber':
            setMonthsNumber(parsedValue.toString());
            break;
          default:
            break;
          }
        }
      }
    };

  const handleSubmit = async () => {
    if (totalPercentage !== 100) {
      alert('La suma de pago inicial, mensualidades y entrega debe dar 100%');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API}/paymentplan/${planToModify ? "update" : "create"}/${planToModify ? planToModify.id : user.id}`, {
        method: planToModify ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          discount: parseFloat(discountPercentage),
          initialPayment: parseFloat(initialPaymentPercentage),
          monthlyPayment: parseFloat(installmentPercentage),
          giveProperty: parseFloat(deliveryPercentage),
          monthsNumber: hasMonthsNumber ? parseInt(monthsNumber, 10) : 0,
        }),
      });

      if (!response.ok) throw new Error('Error al crear el plan de pago');

      await getUserPaymentPlans(user.id);
      closeModal();
    } catch (err) {
      setError('Error al crear el plan de pago: ' + err.message);
      console.error('Error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex justify-center items-center z-[200]">
      <div className="bg-white p-6 rounded-lg w-1/3">
        <h2 className="text-xl font-medium mb-4">Planes de pago</h2>
        {error && (
          <div className="mb-4 p-2 bg-red-100 text-red-700 rounded">
            {error}
          </div>
        )}
        <div>
          <div className="mb-4">
            <label className="block text-sm mb-2">Nombre del plan (generado automáticamente)</label>
            <div className="w-full p-2 border rounded bg-gray-50 text-gray-700 font-mono font-semibold">
              {derivedName}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm mb-2">Porcentaje de descuento</label>
              <input
                type="number"
                className="w-full p-2 border rounded"
                value={discountPercentage}
                onChange={handleInputChange}
                name="discountPercentage"
              />
            </div>
            <div>
              <label className="block text-sm mb-2">Porcentaje de pago inicial</label>
              <input
                type="number"
                className="w-full p-2 border rounded"
                value={initialPaymentPercentage}
                onChange={handleInputChange}
                name="initialPaymentPercentage"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm mb-2">Porcentaje de mensualidades</label>
              <input
                type="number"
                className="w-full p-2 border rounded"
                value={installmentPercentage}
                onChange={handleInputChange}
                name="installmentPercentage"
              />
            </div>
            <div>
              <label className="block text-sm mb-2">Porcentaje a la entrega</label>
              <input
                type="number"
                className="w-full p-2 border rounded"
                value={deliveryPercentage}
                onChange={handleInputChange}
                name="deliveryPercentage"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div className='flex items-center gap-2 h-20'>
              <input
                type="checkbox"
                className="p-2 border rounded"
                checked={hasMonthsNumber}
                onChange={() => setHasMonthsNumber(!hasMonthsNumber)}
                name="hasMonthsNumber"
              />
              <label htmlFor="hasMonthsNumber" className="block text-sm mt-[1px]">Pago por número de meses</label>
            </div>
            {hasMonthsNumber && (
              <div>
                <label className="block text-sm mb-2">No. de meses</label>
                <input
                  type="number"
                  className="w-full p-2 border rounded"
                  value={monthsNumber}
                  onChange={handleInputChange}
                  name="monthsNumber"
                />
              </div>
            )}
          </div>
          <div className="mt-4">
            <p className={`flex text-sm w-full justify-center ${
              totalPercentage !== 100
                ? 'border border-solid border-red-600 text-red-600 bg-red-600/10 rounded-full'
                : 'border border-solid border-nemu-green text-nemu-green bg-nemu-green/15 rounded-full'
            }`}>
              La suma de los porcentajes debe dar 100%
            </p>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={closeModal}
              className="bg-white text-nemu-black border border-solid border-nemu-black py-2 px-4 rounded mr-2"
              disabled={isLoading}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className="bg-blue-500 text-white py-2 px-4 rounded disabled:bg-blue-300"
              disabled={isLoading}
            >
              {isLoading ? 'Creando...' : planToModify ? 'Actualizar datos' : 'Agregar plan de pago'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export { ModalAddPP };